import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react';
import type { createApiClient, PublicIdentity } from '@adieuu/shared';

type ApiClient = ReturnType<typeof createApiClient>;

/** Minimum gap between unforced ML-DSA refetches for one identity. */
export const PQ_REFETCH_THROTTLE_MS = 5_000;

/**
 * Participant profile resolution and signing-key cache for decryption.
 * Refs are stable across renders; {@link resolveParticipants} identity is stable for socket wiring.
 */
export function useConversationParticipantProfiles(
  api: ApiClient,
  setParticipantProfiles: Dispatch<SetStateAction<Record<string, PublicIdentity>>>
) {
  const signingKeyCache = useRef<Record<string, string>>({});
  const pqSigningKeyCache = useRef<Record<string, string>>({});
  const resolvedProfileIds = useRef<Set<string>>(new Set());
  const pqRefetchAttemptAt = useRef<Map<string, number>>(new Map());
  const pqRefetchInFlight = useRef<Map<string, Promise<void>>>(new Map());

  /**
   * Fill empty key-cache entries only. A cached key is never replaced, so a
   * mid-session response cannot change which key verifies a sender.
   */
  const notePeerSigningKeys = useCallback(
    (identityId: string, signingPublicKey?: string, pqSigningPublicKey?: string): boolean => {
      let pqFilled = false;
      if (signingPublicKey && !signingKeyCache.current[identityId]) {
        signingKeyCache.current[identityId] = signingPublicKey;
      }
      if (pqSigningPublicKey) {
        if (!pqSigningKeyCache.current[identityId]) {
          pqSigningKeyCache.current[identityId] = pqSigningPublicKey;
          pqFilled = true;
        }
      } else if (pqSigningKeyCache.current[identityId] === undefined && signingPublicKey) {
        pqSigningKeyCache.current[identityId] = '';
      }
      return pqFilled;
    },
    []
  );

  /** True when {@link refetchPqSigningKey} would start or join a request. */
  const canRefetchPqSigningKey = useCallback(
    (identityId: string, options?: { force?: boolean }): boolean => {
      if (pqSigningKeyCache.current[identityId]) return false;
      if (pqRefetchInFlight.current.has(identityId)) return true;
      if (options?.force) return true;
      const last = pqRefetchAttemptAt.current.get(identityId);
      return last == null || Date.now() - last >= PQ_REFETCH_THROTTLE_MS;
    },
    []
  );

  const refetchPqSigningKey = useCallback(
    (identityId: string, options?: { force?: boolean }): Promise<void> => {
      if (pqSigningKeyCache.current[identityId]) return Promise.resolve();
      const inFlight = pqRefetchInFlight.current.get(identityId);
      if (inFlight) return inFlight;
      if (!canRefetchPqSigningKey(identityId, options)) return Promise.resolve();

      const request = (async () => {
        try {
          const resp = await api.identity.getPublicKeys(identityId);
          pqRefetchAttemptAt.current.set(identityId, Date.now());
          if (resp.data) {
            notePeerSigningKeys(identityId, resp.data.signingPublicKey, resp.data.pqSigningPublicKey);
          }
        } catch {
          // Leave caches untouched and record no attempt so the next trigger retries.
        } finally {
          pqRefetchInFlight.current.delete(identityId);
        }
      })();
      pqRefetchInFlight.current.set(identityId, request);
      return request;
    },
    [api, notePeerSigningKeys, canRefetchPqSigningKey]
  );

  const resolveParticipants = useCallback(
    async (ids: string[]): Promise<Record<string, PublicIdentity>> => {
      const missing = ids.filter((id) => !resolvedProfileIds.current.has(id));
      if (missing.length === 0) return {};

      for (const id of missing) resolvedProfileIds.current.add(id);

      const fetched: Record<string, PublicIdentity> = {};

      await Promise.all(
        missing.map(async (id) => {
          try {
            const resp = await api.identity.getProfile(id);
            if (resp.data) {
              fetched[id] = resp.data;
            }
          } catch {
            resolvedProfileIds.current.delete(id);
          }

          try {
            if (!signingKeyCache.current[id]) {
              const keysResp = await api.identity.getPublicKeys(id);
              if (keysResp.data) {
                signingKeyCache.current[id] = keysResp.data.signingPublicKey;
                pqSigningKeyCache.current[id] = keysResp.data.pqSigningPublicKey ?? '';
              }
            } else if (!pqSigningKeyCache.current[id]) {
              await refetchPqSigningKey(id);
            }
          } catch {
            // Signing keys unavailable
          }
        })
      );

      if (Object.keys(fetched).length > 0) {
        setParticipantProfiles((prev) => ({ ...prev, ...fetched }));
      }

      return fetched;
    },
    [api, setParticipantProfiles, refetchPqSigningKey]
  );

  const refreshParticipantProfile = useCallback(
    async (identityId: string): Promise<void> => {
      resolvedProfileIds.current.delete(identityId);
      try {
        const resp = await api.identity.getProfile(identityId);
        if (resp.data) {
          setParticipantProfiles((prev) => ({ ...prev, [identityId]: resp.data! }));
        }
      } catch {
        setParticipantProfiles((prev) => {
          const next = { ...prev };
          delete next[identityId];
          return next;
        });
      }
    },
    [api, setParticipantProfiles]
  );

  return {
    signingKeyCache,
    pqSigningKeyCache,
    resolveParticipants,
    refreshParticipantProfile,
    notePeerSigningKeys,
    refetchPqSigningKey,
    canRefetchPqSigningKey,
  };
}
